import React, { useEffect, useState } from 'react';
import { Badge, Spinner, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import accreditationPrintingService from '../../../../services/accreditationPrintingService';
import { HugeiconsIcon } from '@hugeicons/react';
import { Delete02Icon, PencilEdit02Icon } from '@hugeicons/core-free-icons';

const PAGE_SIZE = 10;

const TemplateList = ({ onNew, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const res = await accreditationPrintingService.getTemplates({
        page: pageNumber,
        limit: PAGE_SIZE,
        search: searchTerm,
      });
      if (res?.success && res?.data) {
        setTemplates(res.data.list || []);
        setTotalItems(res.data.totalItems || 0);
        setPage(res.data.currentPage || 1);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  // 7. Delete Template
  const handleDeleteTemplate = async (id, name) => {
    const result = await Swal.fire({
      title: 'Delete Template?',
      text: `Are you sure you want to remove template "${name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Delete',
    });

    if (result.isConfirmed) {
      try {
        const res = await accreditationPrintingService.deleteTemplate(id);
        if (res?.success) {
          Swal.fire('Deleted!', 'Template has been deleted.', 'success');
          fetchTemplates();
        }
      } catch (error) {
        Swal.fire('Delete Failed', error.response?.data?.message || error.message, 'error');
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  return (
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-2 w-auto">
              <span className="badge bg-primary-subtle text-primary fs-6 px-3 py-2">
                Total Templates: {totalItems}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2" style={{ maxWidth: '350px' }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search template name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchTemplates(1)}
              />
              <button className="btn btn-primary btn-sm" onClick={() => fetchTemplates(1)}>
                <i className="fa fa-search" />
              </button>
            </div>
          </div>

          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-2">Loading templates...</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-5">
                <i className="fa fa-id-card text-muted opacity-50 display-3 mb-3 d-block" />
                <h5 className="text-secondary">No Templates Configured Yet</h5>
                <p className="text-muted small">
                  Upload an accreditation badge background and mark coordinates for athlete & staff printing.
                </p>
                <button className="btn btn-primary btn-sm mt-2" onClick={onNew}>
                  <i className="fa fa-plus me-1" /> Create Your First Template
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '60px' }}>S.No</th>
                      <th style={{ width: '100px' }}>Preview</th>
                      <th>Template Name</th>
                      <th>Parent Type</th>
                      <th>User Type</th>
                      <th>Template file</th>
                      <th>Design</th>
                      <th>Created Date</th>
                      <th className="text-center" style={{ width: '140px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map((item, idx) => {
                      const coordCount = Array.isArray(item.coordinates)
                        ? item.coordinates.length
                        : typeof item.coordinates === 'string'
                        ? (() => { try { return (JSON.parse(item.coordinates || '[]') || []).length; } catch { return 0; } })()
                        : 0;

                      return (
                        <tr key={item.id}>
                          <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                          <td>
                            {!item.file_url || item.file_type === 'pdf' ? (
                              <div
                                className="bg-primary-subtle text-primary rounded border d-flex align-items-center justify-content-center"
                                style={{ width: '60px', height: '75px', fontSize: '10px', fontWeight: 'bold', textAlign: 'center' }}
                              >
                                {item.file_type === 'pdf' ? 'PDF' : 'STANDARD CARD'}
                              </div>
                            ) : (
                              <img
                                src={item.file_url}
                                alt="Badge preview"
                                className="rounded border object-fit-cover shadow-sm"
                                style={{ width: '60px', height: '75px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                }}
                              />
                            )}
                          </td>
                          <td>
                            <strong className="text-dark d-block">{item.template_name}</strong>
                            <small className="text-muted">ID #{item.id}</small>
                          </td>
                          <td>
                            <span className="badge bg-secondary-subtle text-dark">
                              {item.parentCategory?.category_name_view || `ID: ${item.parent_type_id}`}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-info-subtle text-info-emphasis">
                              {item.userType?.sub_category_name_view || `ID: ${item.user_type_id}`}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {item.file_url ? (item.file_type === 'pdf' ? 'PDF template' : 'Image template') : 'None (standard card)'}
                            </small>
                          </td>
                          <td>
                            <Badge bg="success" pill>
                              {coordCount > 0 ? `Custom: ${coordCount} fields mapped` : 'Standard card'}
                            </Badge>
                          </td>
                          <td>
                            <small className="text-muted">
                              {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                            </small>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="rounded me-1"
                              title="Edit Coordinates"
                              onClick={() => onEdit(item)}
                            >
                              <HugeiconsIcon icon={PencilEdit02Icon} size={18} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="rounded"
                              title="Delete Template"
                              onClick={() => handleDeleteTemplate(item.id, item.template_name)}
                            >
                              <HugeiconsIcon icon={Delete02Icon} size={18} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="card-footer bg-white d-flex justify-content-between align-items-center">
              <small className="text-muted">Page {page} of {totalPages}</small>
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary" disabled={page <= 1 || loading} onClick={() => fetchTemplates(page - 1)}>
                  <i className="fa fa-chevron-left" /> Prev
                </button>
                <button className="btn btn-outline-secondary" disabled={page >= totalPages || loading} onClick={() => fetchTemplates(page + 1)}>
                  Next <i className="fa fa-chevron-right" />
                </button>
              </div>
            </div>
          )}
        </div>
  );
};

export default TemplateList;
